import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import {
    ClassificationAccessRights,
    ControlEntityAccessRights,
    ControlProgramAccessRights,
    RiskEntityAccessRights,
    ResourceAccessRights,
    RiskDirectoryAccessRights,
    SavedReportAccessRights,
    UserGroupAccessRights,
} from "utils/accessRights/models";

export function getLocalizedAccessRightDescription<
    R extends ResourceAccessRights,
    T extends R["accessRights"][number],
>(rights: R, accessRight: T): string {
    switch (rights.type) {
        case "Classification":
            return getLocalizedClassificationAccessRightDescription(
                accessRight
            );
        case "ControlProgram":
            return getLocalizedControlProgramAccessRightDescription(
                accessRight
            );
        case "ControlEntity":
            return getLocalizedControlEntityAccessRightDescription(accessRight);
        case "RiskEntity":
            return getLocalizedEntityAccessRightDescription(accessRight);
        case "RiskDirectory":
            return getLocalizedRiskDirectoryAccessRightDescription(accessRight);
        case "SavedReport":
            return getLocalizedSavedReportAccessRightDescription(accessRight);
        case "UserGroup":
            return getLocalizedUserGroupAccessRightDescription(
                accessRight as UserGroupAccessRights["accessRights"][number]
            );
        case "TestingProgram":
            return getLocalizedTestingProgramAccessRightDescription(
                accessRight
            );
        default:
            return assertNever(rights);
    }
}

function getLocalizedClassificationAccessRightDescription(
    accessRight: ClassificationAccessRights["accessRights"][number]
): string {
    switch (accessRight) {
        case "Admin":
            return t("AccessRights_Description_Classification_Admin");
        case "Edit":
            return t("AccessRights_Description_Classification_Edit");
        case "View":
            return t("AccessRights_Description_Classification_View");
        default:
            return assertNever(accessRight);
    }
}

function getLocalizedControlProgramAccessRightDescription(
    accessRight: ControlProgramAccessRights["accessRights"][number]
): string {
    switch (accessRight) {
        case "Admin":
            return t("AccessRights_Description_ControlProgram_Admin");
        case "Edit":
            return t("AccessRights_Description_ControlProgram_Edit");
        case "View":
            return t("AccessRights_Description_ControlProgram_View");
        default:
            return assertNever(accessRight);
    }
}

function getLocalizedTestingProgramAccessRightDescription(
    accessRight: ControlProgramAccessRights["accessRights"][number]
): string {
    switch (accessRight) {
        case "Admin":
            return t("AccessRights_Description_TestingProgram_Admin");
        case "Edit":
            return t("AccessRights_Description_TestingProgram_Edit");
        case "View":
            return t("AccessRights_Description_TestingProgram_View");
        default:
            return assertNever(accessRight);
    }
}

function getLocalizedControlEntityAccessRightDescription(
    accessRight: ControlEntityAccessRights["accessRights"][number]
): string {
    switch (accessRight) {
        case "Admin":
            return t("AccessRights_Description_ControlEntity_Admin");
        case "Edit":
            return t("AccessRights_Description_ControlEntity_Edit");
        case "View":
            return t("AccessRights_Description_ControlEntity_View");
        default:
            return assertNever(accessRight);
    }
}

function getLocalizedEntityAccessRightDescription(
    accessRight: RiskEntityAccessRights["accessRights"][number]
): string {
    switch (accessRight) {
        case "Admin":
            return t("AccessRights_Description_Entity_Admin");
        case "Edit":
            return t("AccessRights_Description_Entity_Edit");
        case "View":
            return t("AccessRights_Description_Entity_View");
        default:
            return assertNever(accessRight);
    }
}

function getLocalizedRiskDirectoryAccessRightDescription(
    accessRight: RiskDirectoryAccessRights["accessRights"][number]
): string {
    switch (accessRight) {
        case "Admin":
            return t("AccessRights_Description_RiskDirectory_Admin");
        case "Edit":
            return t("AccessRights_Description_RiskDirectory_Edit");
        case "View":
            return t("AccessRights_Description_RiskDirectory_View");
        default:
            return assertNever(accessRight);
    }
}

function getLocalizedSavedReportAccessRightDescription(
    accessRight: SavedReportAccessRights["accessRights"][number]
): string {
    switch (accessRight) {
        case "Admin":
            return t("AccessRights_Description_SavedReport_Admin");
        case "Edit":
            return t("AccessRights_Description_SavedReport_Edit");
        case "View":
            return t("AccessRights_Description_SavedReport_View");
        default:
            return assertNever(accessRight);
    }
}

function getLocalizedUserGroupAccessRightDescription(
    accessRight: UserGroupAccessRights["accessRights"][number]
): string {
    switch (accessRight) {
        case "Admin":
            return t("AccessRights_Description_UserGroup_Admin");
        case "Edit":
            return t("AccessRights_Description_UserGroup_Edit");
        default:
            return assertNever(accessRight);
    }
}
